import DeleteOutlineSharp from '@mui/icons-material/DeleteOutlineSharp'
import CloudUpload from '@mui/icons-material/CloudUpload'
import { IconButton, CircularProgress, Typography, Box } from '@mui/material'
import { useState, useCallback, useEffect } from 'react'
import ImageViewerModal from 'src/consumer/components/Helpers/PictureUploadButton/ImageViewerModal'
import PictureUploadButton from 'src/consumer/components/Helpers/PictureUploadButton/PictureUploadButton'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

export default function MultiplePictureUploadButton({
  imageIds,
  onDeleteImageId,
  onAddImageId,
  aspectWidth,
  aspectHeight,
  imageUploadUrl,
  fileUploadApiParams = {},
  preferCamera = false,
  disabled = false,
  // New props for tracking upload status
  onUploadingChange,
  onUploadComplete,
}) {
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedImageId, setSelectedImageId] = useState(null)
  const [uploadingCount, setUploadingCount] = useState(0)

  // Notify parent when uploading state changes
  useEffect(() => {
    if (onUploadingChange) {
      onUploadingChange(uploadingCount > 0)
    }
  }, [uploadingCount, onUploadingChange])

  const handleUploadStart = useCallback(() => {
    setUploadingCount((prev) => prev + 1)
  }, [])

  const handleUploadEnd = useCallback((success, imageId) => {
    setUploadingCount((prev) => Math.max(0, prev - 1))
    if (success && imageId && onUploadComplete) {
      onUploadComplete(imageId)
    }
  }, [onUploadComplete])

  const handleAddImageWithTracking = useCallback((imageId) => {
    onAddImageId(imageId)
    handleUploadEnd(true, imageId)
  }, [onAddImageId, handleUploadEnd])

  const handleImageClick = (imageId) => {
    setSelectedImageId(imageId)
    setViewerOpen(true)
  }

  const handleCloseViewer = () => {
    setViewerOpen(false)
    setSelectedImageId(null)
  }

  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {imageIds?.map((imageId) => {
        return (
          <div
            key={imageId}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <img
              alt={'imageId'}
              src={getImageLink(imageId)}
              onClick={() => handleImageClick(imageId)}
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '8px',
                border: '1px solid #cecece',
                objectFit: 'cover',
                transition: 'transform 0.2s ease-in-out',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)'
              }}
            />

            {/* Delete button */}
            {!disabled && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation() // Prevent opening viewer
                  onDeleteImageId(imageId)
                }}
                size="small"
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  width: '24px',
                  height: '24px',
                }}
              >
                <DeleteOutlineSharp
                  style={{
                    color: '#f44336',
                    fontSize: '16px',
                  }}
                />
              </IconButton>
            )}
          </div>
        )
      })}
      {!disabled && (
        <PictureUploadButton
          onChange={(e) => handleAddImageWithTracking(e.target.value)}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          imageUploadUrl={imageUploadUrl}
          fileUploadApiParams={{
            ...fileUploadApiParams,
            'sequence-number': (imageIds || [])?.length + 1,
          }}
          preferCamera={preferCamera}
          onUploadStart={handleUploadStart}
          onUploadError={() => handleUploadEnd(false, null)}
        />
      )}
      
      {/* Show uploading indicator */}
      {uploadingCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '8px 12px',
            backgroundColor: 'rgba(67, 56, 202, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(67, 56, 202, 0.2)',
          }}
        >
          <CircularProgress size={16} sx={{ color: '#4338ca' }} />
          <Typography sx={{ fontSize: '12px', color: '#4338ca', fontWeight: 500 }}>
            Uploading {uploadingCount} {uploadingCount === 1 ? 'image' : 'images'}...
          </Typography>
        </Box>
      )}

      <ImageViewerModal
        open={viewerOpen}
        onClose={handleCloseViewer}
        imageId={selectedImageId}
        onDelete={onDeleteImageId}
        disabled={disabled}
      />
    </div>
  )
}
